<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { formatTaxId, getInitials } from '@/utils/formatters';

// --- Interfaces ---
interface Company {
  razao_social: string;
  nome_fantasia?: string;
  tax_id_full: string;
  status: string;
}

interface Props {
  company: Company;
  showStatus?: boolean;
}

// --- Props ---
const props = defineProps<Props>();

// --- Computed ---
/**
 * Semantic palette (mirrors CompanyGeneralData.vue).
 * Baixada is styled as high alarm: we only care about opened companies.
 */
const statusConfig = computed(() => {
  const raw = props.company.status;
  const s = raw?.toUpperCase();
  if (s === 'ATIVA' || s === 'ACTIVE') {
    return {
      icon: 'check_circle',
      iconClass: 'text-emerald-700',
      bgClass: 'bg-emerald-50',
      borderClass: 'border-emerald-200',
      textClass: 'text-emerald-700',
      label: 'Ativa',
    };
  }
  if (s === 'SUSPENSA') {
    return {
      icon: 'pause_circle',
      iconClass: 'text-amber-700',
      bgClass: 'bg-amber-50',
      borderClass: 'border-amber-300',
      textClass: 'text-amber-800',
      label: 'Suspensa',
    };
  }
  if (s === 'INAPTA') {
    return {
      icon: 'warning',
      iconClass: 'text-orange-700',
      bgClass: 'bg-orange-50',
      borderClass: 'border-orange-200',
      textClass: 'text-orange-800',
      label: 'Inapta',
    };
  }
  if (s === 'BAIXADA' || s === 'OUT_OF_BUSINESS') {
    return {
      icon: 'error',
      iconClass: 'text-red-600',
      bgClass: 'bg-red-100',
      borderClass: 'border-red-400',
      textClass: 'text-red-900',
      label: 'Baixada',
    };
  }
  if (s === 'NULA') {
    return {
      icon: 'block',
      iconClass: 'text-rose-800',
      bgClass: 'bg-rose-50',
      borderClass: 'border-rose-200',
      textClass: 'text-rose-900',
      label: 'Nula',
    };
  }
  if (s === 'DESCONHECIDA' || !raw) {
    return {
      icon: 'help',
      iconClass: 'text-slate-500',
      bgClass: 'bg-slate-50',
      borderClass: 'border-slate-200',
      textClass: 'text-slate-600',
      label: 'Desconhecida',
    };
  }
  return {
    icon: 'info',
    iconClass: 'text-indigo-700',
    bgClass: 'bg-indigo-50',
    borderClass: 'border-indigo-200',
    textClass: 'text-indigo-800',
    label: raw,
  };
});

// --- Local Functions ---
const getAvatarColor = (name: string | null | undefined): string => {
  const colors = [
    'bg-slate-600',
    'bg-red-500',
    'bg-orange-500',
    'bg-emerald-500',
    'bg-cyan-500',
    'bg-indigo-500',
  ];
  if (!name) return colors[0]!;
  const charCodeSum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[charCodeSum % colors.length]!;
};
</script>

<template>
  <div class="flex items-center justify-between h-full">
    <div class="flex items-center gap-4 min-w-0">
      <div
        class="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold text-white"
        :class="getAvatarColor(company.razao_social)"
      >
        {{ getInitials(company.razao_social) }}
      </div>
      <div class="min-w-0">
        <p class="font-semibold text-slate-800 truncate">{{ company.razao_social }}</p>
        <p
          v-if="company.nome_fantasia && company.nome_fantasia.length"
          class="text-sm text-slate-500"
        >
          Nome Fantasia: {{ company.nome_fantasia }}
        </p>
        <p class="font-mono text-sm text-slate-500">{{ formatTaxId(company.tax_id_full) }}</p>
      </div>

    </div>
    <div class="flex items-center gap-2 md:gap-4">
      <div
        v-if="showStatus"
        class="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md border"
        :class="[statusConfig.bgClass, statusConfig.borderClass]"
      >
        <BaseIcon :class="statusConfig.iconClass" class="text-[18px]">
          {{ statusConfig.icon }}
        </BaseIcon>
        <span class="text-xs font-bold uppercase tracking-wide" :class="statusConfig.textClass">
          {{ statusConfig.label }}
        </span>
      </div>
      <BaseIcon class="text-slate-400">
        chevron_right
      </BaseIcon>
    </div>
  </div>
</template>

<style scoped>
</style>
